import React, {useState} from 'react';
import {
    Create,
    Edit,
    SimpleForm,
    useInput,
    useRecordContext,
    usePermissions,
    useSaveContext,
    useTranslate,
} from 'react-admin';
import {useFormContext} from 'react-hook-form';
import {Avatar, Box, Chip, MenuItem, Select, Typography} from '@mui/material';
import {DeleteOutline, ExpandMore} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import StatusDot from '../components/statamic/StatusDot';
import StatamicTextInput from '../components/statamic/StatamicTextInput';
import StatamicSelectInput from '../components/statamic/StatamicSelectInput';
import ToggleRow from '../components/statamic/ToggleRow';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import useDeleteConfirm from '../components/statamic/useDeleteConfirm';
import {useCharityChoices} from '../lib/customHooks';
import hasPermission from '../auth/hasPermission';

const CHARITY_ROLE_CHOICES = [
    {id: 'CHARITY_ADMIN',     name: 'Charity Admin'},
    {id: 'CHARITY_VOLUNTEER', name: 'Charity Volunteer'},
];

const SaveAction = ({label}) => {
    const {handleSubmit} = useFormContext();
    const {save, saving} = useSaveContext();
    return (
        <PrimaryButton variant="primary" disabled={saving} onClick={handleSubmit(save)}>
            {saving ? 'Saving…' : label}
        </PrimaryButton>
    );
};

const EmailLockToggle = ({isEditable, onToggle, translate}) => (
    <Box
        onClick={onToggle}
        role="button"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 0.5,
            cursor: 'pointer',
            fontSize: `${t.type.caption.size}px`,
            color: isEditable ? t.color.status.warningSolid : t.color.text.secondary,
            userSelect: 'none',
        }}
    >
        <input type="checkbox" checked={isEditable} readOnly style={{margin: 0}}/>
        {translate('Make editable')}
    </Box>
);

const MultiCharityInput = ({source, label, required, choices = [], defaultValue}) => {
    const {field, fieldState} = useInput({
        source,
        defaultValue,
        validate: required ? (v) => (!v || !v.length ? 'Required' : undefined) : undefined,
    });
    const error = fieldState.isTouched && fieldState.error;
    const value = Array.isArray(field.value) ? field.value : [];

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
            {label && (
                <Box sx={{display: 'flex', alignItems: 'baseline', gap: 0.5}}>
                    <Typography sx={{fontSize: `${t.type.caption.size}px`, fontWeight: 500, color: t.color.text.secondary}}>
                        {label}
                    </Typography>
                    {required && (
                        <Typography component="span" sx={{fontSize: `${t.type.caption.size}px`, color: t.color.status.dangerSolid}}>
                            *
                        </Typography>
                    )}
                </Box>
            )}
            <Select
                {...field}
                multiple
                value={value}
                displayEmpty
                IconComponent={ExpandMore}
                renderValue={(selected) => {
                    if (!selected || selected.length === 0) {
                        return <span style={{color: t.color.text.muted}}>Select…</span>;
                    }
                    return (
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                            {selected.map((id) => {
                                const c = choices.find(ch => ch.id === id);
                                return (
                                    <Chip
                                        key={id}
                                        label={c ? c.name : id}
                                        size="small"
                                        sx={{
                                            height: 22,
                                            fontSize: `${t.type.caption.size}px`,
                                            backgroundColor: t.color.chip?.monoBg || t.color.sidebarHover,
                                            border: `1px solid ${t.color.border.hairline}`,
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    );
                }}
                sx={{
                    fontSize: `${t.type.body.size}px`,
                    backgroundColor: t.color.surface,
                    minHeight: 36,
                    '& .MuiSelect-select': {py: 0.75, px: 1.25, minHeight: 'unset !important'},
                    '& fieldset': {
                        borderColor: `${error ? t.color.status.dangerSolid : t.color.border.default} !important`,
                        borderRadius: `${t.radius.sm}px`,
                    },
                    '&:hover fieldset': {borderColor: `${t.color.border.strong} !important`},
                    '&.Mui-focused fieldset': {
                        borderColor: `${error ? t.color.status.dangerSolid : t.color.accent.primary} !important`,
                        boxShadow: `0 0 0 3px ${error ? t.color.status.dangerTint : t.color.accent.primaryTint}`,
                    },
                    '& .MuiSelect-icon': {color: t.color.text.muted},
                }}
            >
                {choices.map((c) => (
                    <MenuItem key={c.id} value={c.id} sx={{fontSize: `${t.type.body.size}px`}}>
                        {c.name}
                    </MenuItem>
                ))}
            </Select>
            {error && (
                <Typography sx={{fontSize: 11, color: t.color.status.dangerSolid}}>
                    {typeof fieldState.error === 'string' ? fieldState.error : fieldState.error.message}
                </Typography>
            )}
        </Box>
    );
};

const HeaderBar = ({mode, record, translate, canDelete, onDelete}) => {
    const salutation = record?.salutation ? translate(record.salutation) : '';
    const fullName = record
        ? [salutation, record.given_name, record.family_name].filter(Boolean).join(' ') || 'Unnamed'
        : null;

    return (
        <PageHeader
            breadcrumb={{label: translate('Volunteers'), to: '/volunteers'}}
            title={
                mode === 'create' ? (
                    <Box component="span">{translate('Create volunteer')}</Box>
                ) : (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <StatusDot variant={record?.enabled ? 'success' : 'neutral'}/>
                        <Box component="span">{fullName}</Box>
                    </Box>
                )
            }
            actions={
                <>
                    <SaveAction label={mode === 'create' ? translate('Create volunteer') : translate('Save changes')}/>
                    {mode === 'edit' && canDelete && (
                        <PrimaryButton
                            variant="danger"
                            startIcon={<DeleteOutline sx={{fontSize: 16}}/>}
                            onClick={onDelete}
                        >
                            {translate('Delete')}
                        </PrimaryButton>
                    )}
                </>
            }
        />
    );
};

const CoordinatingSection = ({mode, translate, charityChoices, record}) => {
    const renderCharityInput = () => {
        if (mode === 'create') {
            return (
                <StatamicSelectInput
                    source="charity_external_id"
                    label={translate('Charity')}
                    required
                    choices={charityChoices || []}
                />
            );
        }
        // v2 records carry no `charity_ids` key — without an explicit defaultValue the
        // input starts empty and an unmodified save would wipe the coordinated set.
        return (
            <MultiCharityInput
                source="charity_external_ids"
                label={translate('Projects')}
                required
                choices={charityChoices || []}
                defaultValue={record?.charities?.map(c => c.external_id)}
            />
        );
    };

    return (
        <SectionCard title={translate('Coordinating')}>
            <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1}}>
                {renderCharityInput()}
                <StatamicSelectInput
                    source="realm_role"
                    label={translate('Role')}
                    required
                    choices={CHARITY_ROLE_CHOICES}
                />
            </Box>
        </SectionCard>
    );
};

const FormBody = ({mode}) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const deleteConfirm = useDeleteConfirm();

    const charityChoices = useCharityChoices();

    const canDelete = hasPermission(permissions, ['delete'], 'volunteers');

    const [isEmailEditable, setEmailEditable] = useState(mode === 'create');

    if (mode === 'edit' && !record) return null;
    if (!charityChoices) return null;

    const salutation = record?.salutation ? translate(record.salutation) : '';
    const fullName = record
        ? [salutation, record.given_name, record.family_name].filter(Boolean).join(' ') || 'Unnamed'
        : null;

    const avatarUrl = record?.avatar ? `${process.env.REACT_APP_CDN_URL}/${record.avatar}` : undefined;

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <HeaderBar
                mode={mode}
                record={record}
                translate={translate}
                canDelete={canDelete}
                onDelete={deleteConfirm.openDialog}
            />

            {record && (
                <DeleteConfirmDialog
                    open={deleteConfirm.open}
                    onClose={deleteConfirm.closeDialog}
                    resource="volunteers"
                    record={record}
                    recordLabel={fullName}
                />
            )}

            <Box sx={{p: 3, display: 'grid', gridTemplateColumns: '1fr 320px', gap: 2, alignItems: 'start'}}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0}}>
                    <SectionCard title={translate('Basic Information')}>
                        {mode === 'edit' && (
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2, pb: 2, borderBottom: `1px solid ${t.color.border.hairline}`}}>
                                <Avatar
                                    src={avatarUrl}
                                    sx={{
                                        width: 48, height: 48,
                                        bgcolor: t.color.sidebarHover,
                                        color: t.color.text.secondary,
                                        border: `1px solid ${t.color.border.hairline}`,
                                    }}
                                >
                                    {(record?.given_name?.charAt(0) ?? '') + (record?.family_name?.charAt(0) ?? '')}
                                </Avatar>
                                <Typography sx={{fontSize: `${t.type.bodyStrong.size}px`, fontWeight: 600, color: t.color.text.primary}}>
                                    {fullName}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                            <StatamicTextInput source="given_name" label={translate('First name')} required/>
                            <StatamicTextInput source="family_name" label={translate('Last name')} required/>
                        </Box>
                        <Box sx={{mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, alignItems: 'start'}}>
                            <Box>
                                <StatamicTextInput
                                    source="email"
                                    label={translate('email')}
                                    type="email"
                                    required
                                    helperText={mode === 'edit' ? translate('Edit email helper text') : undefined}
                                    key={isEmailEditable ? 'editable' : 'locked'}
                                />
                                {mode === 'edit' && (
                                    <EmailLockToggle
                                        isEditable={isEmailEditable}
                                        onToggle={() => setEmailEditable(v => !v)}
                                        translate={translate}
                                    />
                                )}
                            </Box>
                            {mode === 'create' && hasPermission(permissions, ['create'], 'volunteers') && (
                                <StatamicTextInput
                                    source="password"
                                    label={translate('Tmp password')}
                                    required
                                    defaultValue="TempWw@Nf"
                                />
                            )}
                        </Box>
                    </SectionCard>

                    <CoordinatingSection
                        mode={mode}
                        translate={translate}
                        charityChoices={charityChoices}
                        record={record}
                    />
                </Box>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 16}}>
                    <SectionCard title={translate('Status')}>
                        <ToggleRow
                            source="enabled"
                            label={translate('Enabled')}
                            subtitle={translate('Enabled helper text')}
                            defaultValue={mode === 'create'}
                        />
                    </SectionCard>
                </Box>
            </Box>
        </Box>
    );
};

export const VolunteerEditStatamic = (props) => (
    <Edit {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="show">
        <SimpleForm toolbar={false}>
            <FormBody mode="edit"/>
        </SimpleForm>
    </Edit>
);

export const VolunteerCreateStatamic = (props) => (
    <Create {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="show">
        <SimpleForm toolbar={false}>
            <FormBody mode="create"/>
        </SimpleForm>
    </Create>
);

export default VolunteerEditStatamic;
